import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { ScreenHeader } from '../../components/layout/ScreenHeader';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

import { logoImage } from '../../utils/LogoImageBase64';
import { Briefcase, FileDown, Plus, Trash2, Save as SaveIcon, MapPin, Zap, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, PREVIEW_OFFLINE } from '../../config';
import { useAppContext } from '../../hooks/useAppContext';
import ModernCreateQuotationScreen from './ModernCreateQuotationScreen';

interface QuoteInput {
  capacityKwInput: string;
  pricePerKwInput: string; // default "1100"
  horsepowerInput: string; // عدد الأحصنة (HP)
  deviceTypeInput: string; // رأس كهربائي | غطاس
  deviceCountInput: string; // عدد الأجهزة
}

interface ComputedQuote extends QuoteInput {
  category: 'الأفضل' | 'الجيد' | 'الاقتصادي' | 'الأدنى';
  totalBeforeTax: number;
  vat15: number;
  totalWithTax: number;
  deviceType: 'رأس كهربائي' | 'غطاس';
  deviceCount: number;
  capacityKw: number;
  pricePerKw: number;
  horsepower: number;
  voltage: number;
}
interface SavedQuotationSummary {
  id: string;
  quote_number: string;
  quote_date: string;
  customer_name: string;
  location: string | null;
  mobile: string | null;
  created_at: string;
  total_with_tax: number;
}

const categoryByIndex = (i: number) => ['الأفضل', 'الجيد', 'الاقتصادي', 'الأدنى'][i] as ComputedQuote['category'];

const computeQuote = (q: QuoteInput, index: number): ComputedQuote => {
  const capacity = Number(parseFloat(q.capacityKwInput) || 0);
  const price = Number(q.pricePerKwInput === '' ? 1100 : parseFloat(q.pricePerKwInput));
  const horsepower = Number(parseFloat(q.horsepowerInput) || 0);
  const deviceType = (q.deviceTypeInput === 'غطاس') ? 'غطاس' : 'رأس كهربائي';
  const deviceCount = Number(parseFloat(q.deviceCountInput) || 0) || 1;
  const voltage = 380;
  const totalBeforeTax = Math.round(capacity * price);
  const vat15 = Math.round(totalBeforeTax * 0.15);
  const totalWithTax = totalBeforeTax + vat15;
  return {
    category: categoryByIndex(index),
    capacityKw: capacity,
    pricePerKw: price,
    horsepower,
    totalBeforeTax,
    vat15,
    totalWithTax,
    deviceType,
    deviceCount,
    voltage,
    // include raw inputs for convenience
    capacityKwInput: q.capacityKwInput,
    pricePerKwInput: q.pricePerKwInput,
    horsepowerInput: q.horsepowerInput,
    deviceTypeInput: q.deviceTypeInput,
    deviceCountInput: q.deviceCountInput,
  };
};

const CreateQuotationScreen: React.FC = () => {
  const { designMode } = useAppContext();
  if (designMode === 'modern') {
    return <ModernCreateQuotationScreen />;
  }

  const navigate = useNavigate();
  const [customerName, setCustomerName] = useState('');
  const [location, setLocation] = useState('');
  const [mobile, setMobile] = useState('');
  const [quotes, setQuotes] = useState<QuoteInput[]>([{ capacityKwInput: '', pricePerKwInput: '1100', horsepowerInput: '', deviceTypeInput: 'رأس كهربائي', deviceCountInput: '1' }]);
  const [saving, setSaving] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null);
  const [quoteDate, setQuoteDate] = useState<string | null>(null);
  const [savedQuotations, setSavedQuotations] = useState<SavedQuotationSummary[]>([]);
  const [quotationDetails, setQuotationDetails] = useState<Record<string, { horsepower?: number; device_type?: string; device_count?: number }>>({});

  const addQuote = () => {
    if (quotes.length >= 4) return;
    setQuotes([...quotes, { capacityKwInput: '', pricePerKwInput: '1100', horsepowerInput: '', deviceTypeInput: 'رأس كهربائي', deviceCountInput: '1' }]);
  };

  const removeQuote = (idx: number) => {
    setQuotes(quotes.filter((_, i) => i !== idx));
  };

  const updateQuote = (idx: number, field: keyof QuoteInput, value: string) => {
    const updated = [...quotes];
    if (field === 'capacityKwInput') updated[idx].capacityKwInput = value;
    if (field === 'pricePerKwInput') updated[idx].pricePerKwInput = value;
    if (field === 'horsepowerInput') updated[idx].horsepowerInput = value;
    if (field === 'deviceTypeInput') updated[idx].deviceTypeInput = value;
    if (field === 'deviceCountInput') updated[idx].deviceCountInput = value;
    setQuotes(updated);
  };
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const canGet = !PREVIEW_OFFLINE;
        if (!canGet) return;
        const res = await fetch(`${API_BASE_URL}/quotations?limit=12`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) setSavedQuotations(data as SavedQuotationSummary[]);
        else if (Array.isArray((data as any).items)) setSavedQuotations((data as any).items as SavedQuotationSummary[]);
      } catch { }
    };
    fetchSaved();
  }, []);

  useEffect(() => {
    if (PREVIEW_OFFLINE || savedQuotations.length === 0) return;
    const missing = savedQuotations.filter(q => !quotationDetails[q.id]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const results = await Promise.all(missing.map(async (q) => {
          const res = await fetch(`${API_BASE_URL}/quotations/${q.id}`);
          if (!res.ok) return { id: q.id, first: null } as { id: string; first: any };
          const data = await res.json();
          const first = Array.isArray(data.items) && data.items.length > 0 ? data.items[0] : null;
          return { id: q.id, first } as { id: string; first: any };
        }));
        setQuotationDetails(prev => {
          const next = { ...prev };
          for (const r of results) {
            if (r.first) {
              next[r.id] = {
                horsepower: Number(r.first.horsepower || 0),
                device_type: r.first.device_type,
                device_count: r.first.device_count,
              };
            } else {
              next[r.id] = {};
            }
          }
          return next;
        });
      } catch { }
    })();
  }, [savedQuotations]);

  const computedQuotes: ComputedQuote[] = quotes.map((q, i) => computeQuote(q, i));

  const deleteQuotation = async (id: string) => {
    try {
      const ok = confirm('هل تريد حذف العرض؟');
      if (!ok) return;
      const res = await fetch(`${API_BASE_URL}/quotations/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      setSavedQuotations(prev => prev.filter(q => q.id !== id));
    } catch (e) {
      alert('تعذر حذف العرض');
    }
  };

  const generatePDF = async (data: { quoteNumber: string; date: string; quotes: ComputedQuote[] }) => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '794px';
    container.style.height = '1123px';
    container.style.padding = '40px';
    container.style.direction = 'rtl';
    container.style.fontFamily = 'Cairo, Arial, sans-serif';
    container.style.color = '#353536';
    container.id = 'print-root';

    const d = new Date(data.date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const arDate = d.toLocaleDateString('ar-SA');
    const serialOnlyDigits = String(data.quoteNumber || '1').replace(/\D/g, '');
    const serialPadded = String(serialOnlyDigits ? Number(serialOnlyDigits) : 1).padStart(3, '0');

    const projectLoc = String(location || '').replace(/\s+/g, '');
    const serial = (() => {
      const digits = String(data.quoteNumber || '').replace(/\D/g, '');
      const last3 = digits ? digits.slice(-3) : '';
      return last3 ? last3.padStart(3, '0') : '001';
    })();
    const formattedIdHtml = `<span class=\"accent-location\">${projectLoc}</span>${yyyy}${mm}${dd}${serial}`;
    const headHtml = `
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;align-items:center;margin-top:18px;direction:ltr">
        <div style="text-align:left"></div>
        <div style="text-align:center"></div>
        <div></div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:8px;padding:10px;margin-top:79px;background:rgba(255,255,255,0.9);width:86%;margin-left:auto;margin-right:auto">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px">
          <div>اسم العميل: ${customerName || '-'}</div>
          <div>الموقع: ${location || '-'}</div>
          <div>رقم الجوال: ${mobile || '-'}</div>
          <div style="direction:rtl;unicode-bidi:embed">${formattedIdHtml}</div>
        </div>
      </div>
    `;
    const bgImgHtml = `<img src="/QSREPORT.png.png" crossorigin="anonymous" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:-1" />`;
    const fontCss = `<style>@font-face{font-family:'Cairo';src:url('/fonts/Cairo-Regular.ttf') format('truetype');font-weight:normal;font-style:normal}#print-root, #print-root * { color:#353536 !important }.accent-location{ color:#d00 !important }</style>`;
    const agreedHp = data.quotes.length === 1
      ? (data.quotes[0].horsepower ?? 0)
      : ((data.quotes.find(q => q.category === 'الأفضل')?.horsepower) ?? (data.quotes[0].horsepower ?? 0));
    const agreedType = data.quotes.length === 1
      ? (data.quotes[0].deviceType)
      : ((data.quotes.find(q => q.category === 'الأفضل')?.deviceType) ?? (data.quotes[0].deviceType));
    const agreedCount = data.quotes.length === 1
      ? (data.quotes[0].deviceCount)
      : ((data.quotes.find(q => q.category === 'الأفضل')?.deviceCount) ?? (data.quotes[0].deviceCount));
    const introHtml = `
      <div style="margin-top:32px;font-size:12px;line-height:1.6;font-weight:700;margin-right:64px;width:88%">
        <div style="margin-right:12px">المكرم/ ${customerName || '-'}</div>
        <div style="margin-right:12px">تحية طيبة.</div>
        <div>بناء على الاجتماع الذي عقد معكم لمناقشة آلية تشغيل عدد (<span style="color:#353536">${Number(agreedCount || 0)}</span>) ${agreedType} بقدرة <span style="color:#353536">${Number(agreedHp || 0).toFixed(0)}</span> حصان على الطاقة الشمسية، نرفق لكم عرض السعر يتضمن الجوانب الفنية والتشغيلية</div>
      </div>
    `;

    const tableSingle = (q: ComputedQuote) => `
      <table style="width:100%;border-collapse:collapse;margin-top:160px;font-size:12px;background:#fff">
        <thead>
          <tr style="background:#f79156;color:#353536">
            <th style="padding:8px;text-align:center">البيان</th>
            <th style="padding:8px;text-align:center">القيمة</th>
            <th style="padding:8px;text-align:center"></th>
          </tr>
        </thead>
        <tbody>
          <tr style="background:#fff"><td style="padding:8px;text-align:right">عدد الأحصنة (HP)</td><td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td><td></td></tr>
          <tr style="background:#f9f9f9"><td style="padding:8px;text-align:right">القدرة (kW)</td><td style="padding:8px;text-align:center">${Number(q.capacityKw ?? 0).toFixed(0)}</td><td></td></tr>
          <tr style="background:#fff"><td style="padding:8px;text-align:right">الإجمالي قبل الضريبة</td><td style="padding:8px;text-align:center">${q.totalBeforeTax} ر.س</td><td></td></tr>
          <tr style="background:#f9f9f9"><td style="padding:8px;text-align:right">ضريبة القيمة المضافة (15%)</td><td style="padding:8px;text-align:center">${q.vat15} ر.س</td><td></td></tr>
          <tr style="background:#fff"><td style="padding:8px;text-align:right">الإجمالي شامل الضريبة</td><td style="padding:8px;text-align:center">${q.totalWithTax} ر.س</td><td></td></tr>
        </tbody>
        <tfoot>
          <tr><td style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td></td><td style="padding:8px;text-align:center">${q.deviceCount}</td></tr>
          <tr><td style="padding:8px;text-align:right">المحول</td><td></td><td style="padding:8px;text-align:center">${q.voltage}V</td></tr>
        </tfoot>
      </table>
      `;

    const tableMulti = (arr: ComputedQuote[]) => `
      <table style="width:100%;border-collapse:collapse;margin-top:210px;font-size:12px;background:#fff">
        <thead>
          <tr style="background:#f79156;color:#353536">
            <th style="padding:8px;text-align:center">الفئة</th>
            <th style="padding:8px;text-align:center">HP</th>
            <th style="padding:8px;text-align:center">kW</th>
            <th style="padding:8px;text-align:center">الإجمالي قبل الضريبة</th>
            <th style="padding:8px;text-align:center">الإجمالي شامل الضريبة</th>
            <th style="padding:8px;text-align:center"></th>
          </tr>
        </thead>
        <tbody>
          ${arr.map((q, idx) => `
            <tr style="background:${idx % 2 ? '#f9f9f9' : '#fff'}">
              <td style="padding:8px;text-align:center">${q.category}</td>
              <td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${Number(q.capacityKw ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${q.totalBeforeTax} ر.س</td>
              <td style="padding:8px;text-align:center">${q.totalWithTax} ر.س</td>
              <td></td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr><td colspan="5" style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td style="padding:8px;text-align:center">${arr[0]?.deviceCount ?? 1}</td></tr>
          <tr><td colspan="5" style="padding:8px;text-align:right">المحول</td><td style="padding:8px;text-align:center">${arr[0]?.voltage ?? 380}V</td></tr>
        </tfoot>
      </table>
    `;

    const paymentsHtml = ``;

    const notesHtml = ``;

    container.innerHTML = fontCss + bgImgHtml + headHtml + introHtml + (data.quotes.length === 1 ? tableSingle(data.quotes[0]) : tableMulti(data.quotes)) + paymentsHtml + notesHtml;
    document.body.appendChild(container);
    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const dataUrl = canvas.toDataURL('image/png');
    document.body.removeChild(container);

    doc.addImage(dataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
    doc.save(`Quotation-${projectLoc}${yyyy}${mm}${dd}${serial}.pdf`);
  };

  const handleSave = async () => {
    if (!customerName || quotes.length < 1) {
      alert('يرجى إدخال اسم العميل وإضافة عرض واحد على الأقل.');
      return;
    }
    setSaving(true);
    try {
      const canPost = !PREVIEW_OFFLINE;
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      let localQuoteNumber = `${yyyy}${mm}${dd}001`;
      if (canPost) {
        try {
          const sres = await fetch(`${API_BASE_URL}/serial/today`);
          if (sres.ok) {
            const data = await sres.json();
            if (data && data.serial) localQuoteNumber = String(data.serial);
          }
        } catch { }
      }
      const itemsForPost = computedQuotes.map(q => ({
        category: q.category,
        horsepower: q.horsepower,
        capacity_kw: q.capacityKw,
        price_per_kw: q.pricePerKw,
        total_before_tax: q.totalBeforeTax,
        vat15: q.vat15,
        total_with_tax: q.totalWithTax,
        device_type: q.deviceType,
        device_count: q.deviceCount,
        voltage: q.voltage,
      }));
      if (canPost) {
        const res = await fetch(`${API_BASE_URL}/quotations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quote_number: localQuoteNumber,
            quote_date: `${yyyy}-${mm}-${dd}`,
            customer_name: customerName,
            location,
            mobile,
            items: itemsForPost,
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setQuoteNumber(String(data.quote_number || localQuoteNumber));
        setQuoteDate(String(data.quote_date || now.toISOString()));
        setSavedQuotations(prev => [{
          id: String(data.id),
          quote_number: String(data.quote_number),
          quote_date: String(data.quote_date),
          customer_name: String(data.customer_name),
          location: data.location ?? null,
          mobile: data.mobile ?? null,
          created_at: String(data.created_at || new Date().toISOString()),
          total_with_tax: Number(data.total_with_tax || computedQuotes.reduce((sum, q) => sum + Number(q.totalWithTax || 0), 0)),
        }, ...prev]);
      } else {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const lsKey = 'quotationSerialDaily';
        let serial = 1;
        try {
          const raw = localStorage.getItem(lsKey);
          const parsed = raw ? JSON.parse(raw) : null;
          if (parsed && parsed.date === `${yyyy}-${mm}-${dd}`) {
            serial = Number(parsed.serial || 0) + 1;
          }
          localStorage.setItem(lsKey, JSON.stringify({ date: `${yyyy}-${mm}-${dd}`, serial }));
        } catch { }
        const serialPadded = String(serial).padStart(3, '0');
        setQuoteNumber(localQuoteNumber);
        setQuoteDate(now.toISOString());
        const total = computedQuotes.reduce((sum, q) => sum + Number(q.totalWithTax || 0), 0);
        setSavedQuotations(prev => [{
          id: `${yyyy}${mm}${dd}${serialPadded}`,
          quote_number: localQuoteNumber,
          quote_date: now.toISOString(),
          customer_name: customerName,
          location: location || null,
          mobile: mobile || null,
          created_at: now.toISOString(),
          total_with_tax: total,
        }, ...prev]);
      }
    } catch (err: any) {
      const now = new Date();
      const fallbackNumber = `${now.toISOString().slice(2, 10).replace(/-/g, '')}${String(1).padStart(3, '0')}`;
      setQuoteNumber(fallbackNumber);
      setQuoteDate(now.toISOString());
      const total = computedQuotes.reduce((sum, q) => sum + Number(q.totalWithTax || 0), 0);
      setSavedQuotations(prev => [{
        id: fallbackNumber,
        quote_number: fallbackNumber,
        quote_date: now.toISOString(),
        customer_name: customerName,
        location: location || null,
        mobile: mobile || null,
        created_at: now.toISOString(),
        total_with_tax: total,
      }, ...prev]);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!customerName || quotes.length < 1) {
      alert('يرجى إدخال اسم العميل وإضافة عرض واحد على الأقل.');
      return;
    }
    setSaving(true);
    try {
      const number = quoteNumber;
      const dateIso = quoteDate || new Date().toISOString();
      if (!number) {
        const now = new Date(dateIso);
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const lsKey = 'quotationSerialDaily';
        let serial = 1;
        try {
          const raw = localStorage.getItem(lsKey);
          const parsed = raw ? JSON.parse(raw) : null;
          if (parsed && parsed.date === `${yyyy}-${mm}-${dd}`) {
            serial = Number(parsed.serial || 0) + 1;
          }
          localStorage.setItem(lsKey, JSON.stringify({ date: `${yyyy}-${mm}-${dd}`, serial }));
        } catch { }
        const serialPadded = String(serial).padStart(3, '0');
        const localQuoteNumber = `${yyyy}${mm}${dd}${serialPadded}`;
        setQuoteNumber(localQuoteNumber);
        setQuoteDate(dateIso);
        await generatePDF({ quoteNumber: localQuoteNumber, date: dateIso, quotes: computedQuotes });
      } else {
        await generatePDF({ quoteNumber: number, date: dateIso, quotes: computedQuotes });
      }
    } finally {
      setSaving(false);
    }
  };

  const PreviewTable: React.FC<{ computed: ComputedQuote[] }> = ({ computed }) => (
    <div className="overflow-x-auto">
      {computed.length === 1 ? (
        <table className="w-full text-sm text-center">
          <thead className="bg-slate-100 dark:bg-slate-700">
            <tr>
              <th className="p-2 text-right">البيان</th>
              <th>القيمة</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">الفئة</td><td>{computed[0].category}</td></tr>
            <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">النوع</td><td>{computed[0].deviceType}</td></tr>
            <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">العدد</td><td>{computed[0].deviceCount}</td></tr>
            <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">عدد الأحصنة (HP)</td><td>{computed[0].horsepower ?? 0}</td></tr>
            <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">القدرة (kW)</td><td>{computed[0].capacityKw}</td></tr>

            <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">الإجمالي قبل الضريبة</td><td>{computed[0].totalBeforeTax} ر.س</td></tr>
            <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">ضريبة القيمة المضافة (15%)</td><td>{computed[0].vat15} ر.س</td></tr>
            <tr className="font-bold bg-slate-50 dark:bg-slate-800/50"><td className="p-2 text-right">الإجمالي شامل الضريبة</td><td>{computed[0].totalWithTax} ر.س</td></tr>
          </tbody>
        </table>
      ) : (
        <table className="w-full text-sm text-center">
          <thead className="bg-slate-100 dark:bg-slate-700">
            <tr>
              <th>الفئة</th>
              <th>النوع</th>
              <th>العدد</th>
              <th>عدد الأحصنة (HP)</th>
              <th>القدرة (kW)</th>
              <th>الإجمالي قبل الضريبة</th>
              <th>الإجمالي شامل الضريبة</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {computed.map((q, i) => (
              <tr key={i} className="border-b dark:border-slate-700">
                <td>{q.category}</td>
                <td>{q.deviceType}</td>
                <td>{q.deviceCount}</td>
                <td>{q.horsepower ?? 0}</td>
                <td>{q.capacityKw}</td>
                <td>{q.totalBeforeTax} ر.س</td>
                <td className="font-bold">{q.totalWithTax} ر.س</td>
                <td></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 dark:bg-slate-800/40">
              <td colSpan={7} className="p-2 text-right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td>
              <td className="p-2 text-center">{computed[0]?.deviceCount ?? 1}</td>
            </tr>
            <tr className="bg-slate-50 dark:bg-slate-800/40">
              <td colSpan={7} className="p-2 text-right">المحول</td>
              <td className="p-2 text-center">{computed[0]?.voltage ?? 380}V</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <ScreenHeader
        icon={Briefcase}
        title="إنشاء عرض سعر"
        colorClass="bg-nav-project"
        onBack="/projects"
      />

      <Card>
        <CardHeader>
          <CardTitle>بيانات العميل</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">اسم العميل</label>
              <Input value={customerName} onChange={e => setCustomerName(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">الموقع</label>
              <Input value={location} onChange={e => setLocation(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">رقم الجوال</label>
              <Input value={mobile} onChange={e => setMobile(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>العروض</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {quotes.map((q, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end border-b pb-4">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium mb-1">الفئة</label>
                  <Input value={categoryByIndex(idx)} readOnly />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium mb-1">عدد الأحصنة (HP)</label>
                  <Input type="number" inputMode="decimal" min="0" value={q.horsepowerInput} placeholder="مثال: 10" onChange={e => updateQuote(idx, 'horsepowerInput', e.target.value)} />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium mb-1">النوع</label>
                  <select className="w-full border rounded px-2 py-2 text-sm" value={q.deviceTypeInput} onChange={e => updateQuote(idx, 'deviceTypeInput', e.target.value)}>
                    <option value="رأس كهربائي">رأس كهربائي</option>
                    <option value="غطاس">غطاس</option>
                  </select>
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium mb-1">عدد المضخات المراد تشغيلها على الطاقة الشمسية</label>
                  <Input type="number" inputMode="decimal" min="1" value={q.deviceCountInput} placeholder="مثال: 1" onChange={e => updateQuote(idx, 'deviceCountInput', e.target.value)} />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium mb-1">القدرة (kW)</label>
                  <Input type="number" inputMode="decimal" min="0" value={q.capacityKwInput} placeholder="مثال: 7.5" onChange={e => updateQuote(idx, 'capacityKwInput', e.target.value)} />
                </div>
                <div className="md:col-span-1">
                  <label className="block text_sm font-medium mb-1">السعر/كيلوواط (افتراضي 1100)</label>
                  <Input type="number" inputMode="decimal" min="0" value={q.pricePerKwInput} placeholder="1100" onChange={e => updateQuote(idx, 'pricePerKwInput', e.target.value)} />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium mb-1">المحول (الفولت)</label>
                  <Input value="380V" readOnly />
                </div>
                <div className="flex gap-2 md:justify-end">
                  {quotes.length > 1 && (
                    <Button variant="outline" onClick={() => removeQuote(idx)} icon={<Trash2 size={16} />}>حذف</Button>
                  )}
                </div>
              </div>
            ))}
            <div className="flex justify-between">
              <Button onClick={addQuote} disabled={quotes.length >= 4} icon={<Plus size={16} />}>+ إضافة عرض</Button>
              <div className="flex items-center gap-2">
                <Button onClick={handleSave} disabled={saving || !customerName} icon={<SaveIcon size={16} />}>حفظ</Button>
                <Button onClick={handleExport} icon={<FileDown size={16} />} disabled={saving || !customerName}>تصدير</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {savedQuotations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>العروض المحفوظة</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedQuotations.map(item => (
                <Card
                  key={item.id}
                  className="relative cursor-pointer group bg-slate-100 dark:bg-slate-800/50"
                  onClick={() => navigate(`/quotations/${item.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') navigate(`/quotations/${item.id}`); }}
                >
                  <CardHeader className="flex items-center justify-between">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{item.customer_name}</CardTitle>
                      <div className="text-xs text-slate-500 truncate">{item.location || '-'}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); deleteQuotation(item.id); }}
                      title="حذف العرض"
                      icon={<Trash2 size={16} />}
                    />
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 flex items-center gap-1"><MapPin size={14} /> موقع العميل</span>
                      <span className="font-semibold truncate max-w-[55%]">{item.location || '-'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 flex items-center gap-1"><Zap size={14} /> عدد الأحصنة (HP)</span>
                      <span className="font-semibold">{quotationDetails[item.id]?.horsepower ?? '-'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 flex items-center gap-1"><Calendar size={14} /> التاريخ</span>
                      <span className="font-semibold">{new Date(item.quote_date).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">الإجمالي شامل الضريبة</span>
                      <span className="font-semibold">{Number(item.total_with_tax || 0).toLocaleString('en-GB')} ر.س</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>تفاصيل العرض المالي</CardTitle>
        </CardHeader>
        <CardContent>
          <PreviewTable computed={computedQuotes} />
          {quoteNumber && quoteDate && (
            <div className="mt-3 text-sm text-slate-600">تم توليد رقم العرض: {quoteNumber} — التاريخ: {new Date(quoteDate).toLocaleDateString('ar-SA')}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateQuotationScreen;
